
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { FormData } from "../types";
import { z } from "zod";

const phoneRegex = /^[+]?[\d\s\-()]*$/;

const createFormSchema = (language: string) => z.object({
  companyName: z.string()
    .trim()
    .min(1, language === 'en' ? "Company name is required" : "Cégnév megadása kötelező")
    .max(200, language === 'en' ? "Company name is too long" : "Cégnév túl hosszú")
    .refine(val => !/<[^>]*>/.test(val), language === 'en' ? "Invalid characters" : "Érvénytelen karakterek"),
  contactName: z.string()
    .trim()
    .min(1, language === 'en' ? "Contact name is required" : "Kapcsolattartó neve kötelező")
    .max(100, language === 'en' ? "Contact name is too long" : "Kapcsolattartó neve túl hosszú")
    .refine(val => !/<[^>]*>/.test(val), language === 'en' ? "Invalid characters" : "Érvénytelen karakterek"),
  email: z.string()
    .trim()
    .min(1, language === 'en' ? "Email is required" : "E-mail cím megadása kötelező")
    .email(language === 'en' ? "Invalid email address" : "Érvénytelen e-mail cím")
    .max(255, language === 'en' ? "Email is too long" : "E-mail cím túl hosszú")
    .transform(val => val.toLowerCase()),
  phone: z.string()
    .max(30, language === 'en' ? "Phone number is too long" : "Telefonszám túl hosszú")
    .refine(val => val === '' || phoneRegex.test(val), language === 'en' ? "Invalid phone number format" : "Érvénytelen telefonszám formátum")
    .optional()
    .or(z.literal("")),
  businessType: z.string().max(100).optional().or(z.literal("")),
  message: z.string()
    .max(2000, language === 'en' ? "Message is too long" : "Üzenet túl hosszú")
    .refine(val => !/<script/i.test(val), language === 'en' ? "Invalid content" : "Érvénytelen tartalom")
    .optional()
    .or(z.literal("")),
  planningService: z.boolean()
});

const initialFormData: FormData = {
  companyName: "",
  contactName: "",
  email: "",
  phone: "",
  businessType: "",
  message: "",
  planningService: false
};

export type FormErrors = Partial<Record<keyof FormData, string>>;

export const useContactForm = () => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    setFormData({
      ...formData,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value
    });
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formSchema = createFormSchema(language);
    const result = formSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof FormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([{
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone || null,
          business_type: formData.businessType || null,
          message: formData.message || null,
          planning_service: formData.planningService
        }]);
      
      if (error) {
        console.error('Error submitting form:', error);
        toast({
          title: language === 'en' ? "An error occurred!" : "Hiba történt!",
          description: language === 'en' 
            ? "We couldn't process your message. Please try again later." 
            : "Nem sikerült feldolgozni az üzenetet. Kérjük, próbálja újra később.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: language === 'en' ? "Message sent!" : "Üzenet elküldve!",
        description: language === 'en'
          ? "Thank you for your interest! We'll get back to you soon."
          : "Köszönjük érdeklődését! Hamarosan felvesszük Önnel a kapcsolatot."
      });

      setFormData(initialFormData);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: language === 'en' ? "An error occurred!" : "Hiba történt!",
        description: language === 'en'
          ? "We couldn't process your subscription. Please try again later."
          : "Nem sikerült feldolgozni a feliratkozást. Kérjük, próbálja újra később.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    errors,
    handleChange,
    handleSubmit
  };
};
